"use client";

import React, { useState, useEffect } from "react";
import { X, Mail, Lock, User, Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

interface BranchAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  branchSlug: string;
  subdomain?: string;
  onAuthSuccess?: (customer: any, token: string) => void;
}

export default function BranchAuthModal({
  isOpen,
  onClose,
  branchSlug,
  subdomain = "",
  onAuthSuccess,
}: BranchAuthModalProps) {
  const [tab, setTab] = useState<"login" | "register">("login");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGooglePrompt, setShowGooglePrompt] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  const [googleEmail, setGoogleEmail] = useState("");
  const [googleName, setGoogleName] = useState("");
  const [googlePhone, setGooglePhone] = useState("");

  useEffect(() => {
    setError("");
    setSuccessMsg("");
    setShowGooglePrompt(false);
  }, [tab, isOpen]);

  if (!isOpen) return null;

  const saveBranchSession = (data: any, fallbackEmail: string, fallbackName: string, fallbackPhone?: string) => {
    const access = data?.tokens?.access || data?.access || data?.token || "";
    const refresh = data?.tokens?.refresh || data?.refresh || "";
    const rawUser = data?.user || data?.customer || {};
    const cleanEmail = (rawUser.email || data?.customer?.email || fallbackEmail || "").trim().toLowerCase();

    const customerObj = {
      id: rawUser.id || data?.customer?.id || Date.now(),
      email: cleanEmail,
      first_name: rawUser.first_name || data?.customer?.first_name || fallbackName.split(" ")[0] || "",
      last_name: rawUser.last_name || data?.customer?.last_name || fallbackName.split(" ").slice(1).join(" ") || "",
      name: rawUser.name || fallbackName || cleanEmail.split("@")[0],
      phone: rawUser.phone || data?.customer?.phone || fallbackPhone || "",
      branch_slug: branchSlug,
      subdomain,
    };

    if (typeof window !== "undefined") {
      localStorage.setItem(`branch_customer_${branchSlug}`, JSON.stringify(customerObj));
      localStorage.setItem("xylo_user", JSON.stringify(customerObj));
      if (access) {
        localStorage.setItem(`branch_token_${branchSlug}`, access);
        localStorage.setItem("access_token", access);
        localStorage.setItem("token", access);
      }
      if (refresh) {
        localStorage.setItem("refresh_token", refresh);
      }
    }

    if (onAuthSuccess && access) {
      onAuthSuccess(customerObj, access);
    }

    setSuccessMsg(tab === "login" ? "সফলভাবে লগইন হয়েছে!" : "রেজিস্ট্রেশন সম্পন্ন হয়েছে!");
    setTimeout(() => {
      onClose();
      window.location.reload();
    }, 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    const headers: Record<string, string> = {};
    if (subdomain) headers["X-Tenant-Subdomain"] = subdomain;

    try {
      if (tab === "login") {
        const res = await api.post(
          "/customers/branch-login/",
          {
            email: cleanEmail,
            password,
            branch_slug: branchSlug,
            subdomain,
          },
          { headers }
        );
        saveBranchSession(res.data, cleanEmail, firstName);
      } else {
        const fullName = `${firstName} ${lastName}`.trim() || cleanEmail.split("@")[0];
        const res = await api.post(
          "/customers/branch-register/",
          {
            email: cleanEmail,
            password,
            first_name: firstName.trim() || fullName.split(" ")[0],
            last_name: lastName.trim() || fullName.split(" ").slice(1).join(" "),
            name: fullName,
            phone: phone.trim(),
            branch_slug: branchSlug,
            subdomain,
          },
          { headers }
        );
        saveBranchSession(res.data, cleanEmail, fullName, phone.trim());
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "লগইন বা রেজিস্ট্রেশন সম্পন্ন করা যায়নি।"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleBranchAuth = async () => {
    setError("");
    const cleanEmail = googleEmail.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("অনুগ্রহ করে সঠিক Gmail অ্যাড্রেস দিন।");
      return;
    }

    setGoogleLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (subdomain) headers["X-Tenant-Subdomain"] = subdomain;

      const fullName = googleName.trim() || cleanEmail.split("@")[0];
      const res = await api.post(
        "/customers/branch-google/",
        {
          email: cleanEmail,
          name: fullName,
          first_name: fullName.split(" ")[0],
          last_name: fullName.split(" ").slice(1).join(" "),
          phone: googlePhone.trim(),
          branch_slug: branchSlug,
          subdomain,
        },
        { headers }
      );
      saveBranchSession(res.data, cleanEmail, fullName, googlePhone.trim());
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Google অথেনটিকেশন ব্যর্থ হয়েছে।");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 shadow-2xl border border-gray-100">
        <button
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {tab === "login" ? "ব্রাঞ্চ কাস্টমার লগইন" : "ব্রাঞ্চ অ্যাকাউন্ট তৈরি করুন"}
          </h2>
          <p className="mt-1 text-xs text-gray-500">Branch: {branchSlug}</p>
        </div>

        <div className="mb-5 grid grid-cols-2 rounded-2xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setTab("login")}
            className={`rounded-xl py-2 text-sm font-semibold transition ${
              tab === "login" ? "bg-white text-emerald-600 shadow-sm" : "text-gray-600"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setTab("register")}
            className={`rounded-xl py-2 text-sm font-semibold transition ${
              tab === "register" ? "bg-white text-emerald-600 shadow-sm" : "text-gray-600"
            }`}
          >
            Register
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-600">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            <span>{successMsg}</span>
          </div>
        )}

        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowGooglePrompt(!showGooglePrompt)}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>{tab === "login" ? "Continue with Google" : "Register with Google"}</span>
          </button>

          {showGooglePrompt && (
            <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-2.5">
              <input
                type="email"
                placeholder="আপনার Gmail দিন (যেমন: hasibsk@gmail.com)"
                value={googleEmail}
                onChange={(e) => setGoogleEmail(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              />
              <input
                type="text"
                placeholder="আপনার নাম"
                value={googleName}
                onChange={(e) => setGoogleName(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              />
              <input
                type="tel"
                placeholder="মোবাইল নম্বর (ঐচ্ছিক)"
                value={googlePhone}
                onChange={(e) => setGooglePhone(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
              />
              <button
                type="button"
                disabled={googleLoading}
                onClick={handleGoogleBranchAuth}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-xs font-bold text-white hover:bg-emerald-700"
              >
                {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span>Google দিয়ে নিশ্চিত করুন</span>
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {tab === "register" && (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    required
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-3 text-sm text-gray-900"
                  />
                </div>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-3 text-sm text-gray-900"
                  />
                </div>
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-3 text-sm text-gray-900"
                />
              </div>
            </>
          )}

          <div className="relative">
            <Mail className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="email"
              required
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-3 text-sm text-gray-900"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-3 text-sm text-gray-900"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white hover:bg-emerald-700 transition"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <span>{tab === "login" ? "Sign In" : "Register"}</span>}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}